/**
 * Serializer - object serialization and deserialization. Dumper/Loader implementation
 * with support for prefabs, class aliases, and property metadata.
 */

import * as THREE from 'three';

import { CurrentSerializationVersion, SerializationVersion } from '../../const.js';
import { ClassRegistry } from '../../systems/ClassRegistry.js';
import { objectInitOptionsTracker } from '../ObjectInitOptionsTracker.js';

import { getAllPropertiesFromProto, getDefaultObject } from './cdo.js';
import {
  getClassForDeserialization,
  getClassSerializeName,
} from './clsAlias.js';
import { createDumper, registerDumper, registerLoader } from './factories.js';
import { instantiatePrefab } from './prefab.js';
import { PrefabManager } from './prefabManager.js';
import { ISerializableObjectTag, STALE_PREFAB_CHILD_ARRAY_SLOT } from './serializable.js';
import {
  allowPopulateExistingInstance,
  getDeserializeFn,
  getSerializeFn,
  getStaticDeserializeFn
} from './specializationUtils.js';
import { DumperFlags, DumpValueResult, LoaderFlags, serializationVersionKey } from './types.js';
import './specialization.js';  // make sure specializations are registered

import type { PropertyMetadata } from './decorator.js';
import type { IDumper, ILoader } from './factories.js';
import type { ISerializableObject } from './serializable.js';
import type {
  BaseInfo,
  ClassInfo,
  DumperOptions,
  LoaderOptions,
  PrefabInfo,
  Ref,
  RefType,
  RootSerializedData,
  SerializedArrayNode,
  SerializedData,
  SerializedObjectNode,
  SerializedSubObject,
  SerializedValue,
} from './types.js';
import type { Constructor } from '../../systems/ClassRegistry.js';

export { STALE_PREFAB_CHILD_ARRAY_SLOT } from './serializable.js';

const VERSION = CurrentSerializationVersion;
const UNREGISTERED_CLASS_PREFIX = 'UNREGISTERED.';

/**
 * Recursively sorts object keys alphabetically for stable JSON.stringify output.
 * Arrays are preserved but their object elements are sorted.
 */
function sortKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(sortKeys);
  }

  const sortedObj: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    sortedObj[key] = sortKeys(obj[key as keyof typeof obj]);
  }
  return sortedObj;
}

function isClassInfo(baseInfo: BaseInfo): baseInfo is ClassInfo {
  return '$class' in baseInfo;
}

function isPrefabInfo(baseInfo: BaseInfo): baseInfo is PrefabInfo {
  return '$path' in baseInfo;
}

function isSerializableObject(obj: any): obj is ISerializableObject {
  return obj && typeof obj === 'object' && obj[ISerializableObjectTag] === true;
}

let actorBaseClass: Constructor | undefined;

function isActorConstructor(cls: Constructor): boolean {
  actorBaseClass ??= ClassRegistry.getClass('ENGINE.Actor');
  return (
    cls.prototype === actorBaseClass.prototype
    || actorBaseClass.prototype.isPrototypeOf(cls.prototype)
  );
}

// Init options come from two call sites: normal dumps store a SerializedValue,
// while network construction data stores a standalone SerializedData document.
function isSerializedData(value: unknown): value is SerializedData {
  return (
    value !== null &&
    typeof value === 'object' &&
    serializationVersionKey in value &&
    '$root' in value
  );
}


function deepishEqual(a: unknown, b: unknown, maxDepth = 3): boolean {
  function eq(a: unknown, b: unknown, depth: number, maxDepth: number): boolean {
    if (depth > maxDepth) {
    // If we *reach* depth limit, treat as different
      return false;
    }

    // Fast path: identity or strict equality
    if (a === b) return true;

    // Handle case where types differ
    if (typeof a !== typeof b) return false;

    // if both functions, consider them equal
    if (typeof a === 'function' && typeof b === 'function') {
      return true;
    }

    // Handle primitives
    if (a === null || b === null || typeof a !== 'object') return false;

    // At this point both a and b are objects (and not null)
    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;

    // Arrays
    const aIsArray = Array.isArray(aObj);
    const bIsArray = Array.isArray(bObj);
    if (aIsArray !== bIsArray) return false;
    if (aIsArray && bIsArray) {
      if (aObj.length !== bObj.length) return false;
      for (let i = 0; i < aObj.length; i++) {
        if (!eq(aObj[i], bObj[i], depth + 1, maxDepth)) return false;
      }
      return true;
    }

    // Objects
    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);
    if (aKeys.length !== bKeys.length) return false;

    for (const key of aKeys) {
      if (!(key in bObj)) return false;
      if (!eq(aObj[key], bObj[key], depth + 1, maxDepth)) return false;
    }

    return true;
  }

  return eq(a, b, 0, maxDepth);
}


/**
 * The serialized data will either be $root + $objects, or $prefab + $diff
 * if it's $prefab + $diff, the prefab data can be another prefab, forming a chain of prefabs
 *
 * A typical result of serialized (SerializedData) would look something like this:
 * {
 *   $prefab: {
 *    $path: '@engine/prefabs/Prefab1.prefab.json'
 *   },
 *   $diff: {
 *     // only the differences from the prefab1
 *   }
 * }
 * Once all prefabs with paths are resolved, the final expanded data would look like this:
 * {
 *   $prefab: {
 *    $path: '@engine/prefabs/Prefab1.prefab.json',
 *    $data: {
 *      // yet another prefab data here
 *     $prefab: {
 *       $path: '@engine/prefabs/Prefab2.prefab.json',
 *       $data: {
 *         // a prefab that's based on Actor instead of another prefab
 *         $root: {
 *           $class: 'ENGINE.Actor', ...
 *         },
 *         $objects: [
 *           // serialized objects here
 *         ]
 *       }
 *     },
 *     $diff: {
 *       // only the differences from the prefab2
 *     }
 *    }
 *   },
 *   $diff: {
 *     // only the differences from the prefab1
 *   }
 * }
 */

// make sure it's kept in sync with RefType
function _isRef(v: any): v is { $ref: RefType } {
  return v && typeof v === 'object' && typeof v.$ref === 'number';
}

function getAllProperties(obj: object): Map<string, PropertyMetadata> {
  let proto = Object.getPrototypeOf(obj);
  return getAllPropertiesFromProto(proto);
}

function shouldSerializeRespectingMetadata(obj: object): boolean {
  const properties = getAllProperties(obj);
  const hasDecoratedProperties = properties.size > 0;
  if (hasDecoratedProperties) {
    return true;
  }

  const className = ClassRegistry.getClassName(obj);
  const isClassRegistered = !!className;
  return isClassRegistered;
}

class Serializer {
  protected get isDumping(): boolean {return false;}

}

/**
 * Dumper serializes objects into a structured format, handling references and class instances.
 * If an object is referenced more than once, it will be assigned an ID and stored in the $objects array as SerializedObject[].
 * If an object is a class instance, it will be stored with its class name, constructor arguments, and properties, basically an inlined `SerializedObject`
 * otherwise, it will be inlined directly.
 * The output format is `SerializedData`, with always a $root field, and optionally a $objects array.
 * The following are possible outputs:
 * plain objects: {a: 1, b: {x: 2, y: 3}} -> {$root: {a: 1, b: {x: 2, y:3}}}
 * with shared/circular references: {a: obj1, b: obj1, c: {x: 1}} -> {$root: {a: {$ref: 1}, b: {$ref: 1}, c: {x: 1}}, $objects: [{ $id: 1, $value: {fields of obj1...} }]}
 * with class: THREE.Vector3(1,2,3) -> {$root: { $class: 'THREE.Vector3', x:1, y:2, z:3 }}
 */

class Dumper extends Serializer implements IDumper {
  private seen = new WeakMap<object, RefType>();
  private objects: SerializedSubObject[] = [];
  private idCounter = 1;
  private currentRecord: SerializedSubObject | null = null;
  private defaultValues: SerializedObjectNode | null = null;
  private refCount = new Map<RefType, number>();
  private pathStack: string[] = [];
  private flags: DumperFlags;

  constructor(options?: DumperOptions) {
    super();
    this.flags = options?.flags ?? DumperFlags.None;
    if (this.hasFlags(DumperFlags.Inline)) {
      // make sure AsPrefab is also set
      this.flags |= DumperFlags.AsPrefab;
    }
  }

  protected override get isDumping(): boolean { return true; }

  public hasFlags(flags: DumperFlags): boolean {
    return (this.flags & flags) === flags;
  }

  public get dumpingAsPrefab() {
    return this.hasFlags(DumperFlags.AsPrefab);
  }

  public dump(obj: any): SerializedData {
    if (this.idCounter > 1 || this.objects.length > 0 || this.currentRecord) {
      throw new Error('Dumper instances cannot be reused.');
    }

    const startTime = performance.now();

    let realPrefabPath: any;
    if (this.hasFlags(DumperFlags.Inline) && obj?.$prefab) {
      // modify existing prefab to its parent
      realPrefabPath = obj.$prefab;
      const baseInfo = PrefabManager.getPrefabBaseInfo(realPrefabPath.$path);
      if (baseInfo && isPrefabInfo(baseInfo)) {
        obj.$prefab = baseInfo;
      } else if (baseInfo && isClassInfo(baseInfo)) {
        delete obj.$prefab;
      }
    }

    try {
      const exported = this._dump(obj);
      const objects = this.objects;

      // result with all data fields serialized
      const rootData: RootSerializedData = {
        $root: exported,
      };
      if (objects.length > 0) {
        rootData.$objects = objects;
      }

      // optimize to make objects that are only referenced once inlined
      const optimizedExpandedResult = this._optimize(rootData);

      // generate diff against base data if applicable
      // const diff = deepDiff(expandedBaseData, optimizedExpandedResult);
      const result: SerializedData = {
        [serializationVersionKey]: VERSION,
        ...optimizedExpandedResult
      };

      // Sort keys recursively for stable JSON.stringify output
      return sortKeys(result) as SerializedData;
    } finally {
      if (realPrefabPath) {
        obj.$prefab = realPrefabPath;
      }
      const endTime = performance.now();
      const duration = endTime - startTime;
      if (duration > 50) {
        console.log(`📦Dumper: Dumping took time ${duration.toFixed(2)}ms`);
      }
    }
  }

  private _optimize(serializedData: RootSerializedData): RootSerializedData {
    // flatten the objects that are only referenced once
    const objectsMap = new Map<RefType, SerializedSubObject>();
    if (serializedData.$objects) {
      for (const record of serializedData.$objects) {
        objectsMap.set(record.$id!, record);
      }
    }

    // step 1: inline objects that are only referenced once
    const inlineObject = (obj: SerializedValue): void => {
      if (typeof obj !== 'object' || obj === null) {
        return;
      }

      const objNode = obj as SerializedObjectNode;
      for (const [k, v] of Object.entries(objNode)) {
        if (_isRef(v)) {
          const refId = v.$ref;
          const refCount = this.refCount.get(refId)!;
          if (refCount === 1) {
            const record = objectsMap.get(refId)!;
            if (record.$value && typeof record.$value === 'object' && !Array.isArray(record.$value)) {
              const { $value = {}, $id, ...rest } = record;
              objNode[k] = {
                ...rest,
                ...$value,
              };
            } else {
              objNode[k] = record.$value;
            }
          }
        }

        inlineObject(objNode[k] as SerializedValue);
      }
    };

    // start inlining from the root
    inlineObject(serializedData);

    // step 2: rebuild the $objects array
    if (serializedData.$objects) {
      serializedData.$objects = serializedData.$objects?.filter(record => {
        const refCount = this.refCount.get(record.$id!)!;
        return refCount > 1;
      });

      if (serializedData.$objects.length === 0) {
        delete serializedData.$objects;
      }
    }

    // step 3: optional, reassign IDs to be sequential, this is mainly to make tests easier compose expected output
    const idMap = new Map<RefType, RefType>();
    let newIdCounter = 1;
    if (serializedData.$objects) {
      for (const record of serializedData.$objects) {
        const oldId = record.$id!;
        const newId = newIdCounter++;
        idMap.set(oldId, newId);
        record.$id = newId;
      }
    }

    const remapRefs = (obj: SerializedValue): void => {
      if (typeof obj !== 'object' || obj === null) {
        return;
      }

      const objNode = obj as SerializedObjectNode;
      for (const [k, v] of Object.entries(objNode)) {
        if (_isRef(v)) {
          v.$ref = idMap.get(v.$ref)!;
          if (!v.$ref) {
            throw new Error(`Dumper: remapRefs failed to find new ID for ref ${v.$ref}`);
          }
        }
        remapRefs(objNode[k] as SerializedValue);
      }
    };

    // remap refs in the root object
    remapRefs(serializedData);

    // step 4: replace { $base: {$class: '...' } } with just {$bc: '...'} for smaller size
    const shortenClassBaseInfo = (obj: SerializedValue): void => {
      if (typeof obj !== 'object' || obj === null) {
        return;
      }
      const objNode = obj as SerializedObjectNode;
      for (const [k, v] of Object.entries(objNode)) {
        if (v && typeof v === 'object' && '$base' in v) {
          const childNode = v as SerializedObjectNode;
          const baseInfo = childNode.$base as BaseInfo;
          if (isClassInfo(baseInfo)) {
            childNode.$bc = baseInfo.$class;
            delete childNode.$base;
          }
        }
        shortenClassBaseInfo(v as SerializedValue);
      }
    };
    shortenClassBaseInfo(serializedData);


    // step 5: handle float numbers to reduce size
    const processNumbers = (obj: SerializedValue): void => {
      if (typeof obj !== 'object' || obj === null) {
        return;
      }

      const objNode = obj as SerializedObjectNode;
      for (const [k, v] of Object.entries(objNode)) {
        if (typeof v === 'number') {
          if (Number.isInteger(v)) {
            objNode[k] = v;
          } else {
            objNode[k] = Math.round(v * 1000000) / 1000000;
          }
        } else {
          processNumbers(v as SerializedValue);
        }
      }
    };

    processNumbers(serializedData);

    return serializedData;
  }

  // instanceKey is the key in the parent object, used for getting the value on prefab
  // return true if anything is dumped
  public dumpValue(key: string, value: any, instanceKey?: string, metadata?: PropertyMetadata): DumpValueResult {
    if (metadata?.transient) {
      // skip transient properties
      return DumpValueResult.SkippedTransient;
    }
    if (metadata?.transientOnPrefab && this.dumpingAsPrefab) {
      // skip transientOnPrefab properties when dumping as prefab
      return DumpValueResult.SkippedTransient;
    }
    if (
      (isSerializableObject(value) || value instanceof THREE.Object3D)
      && value.isTransient
      && value.isTransient()
    ) {
      // transient object, skip it
      return DumpValueResult.SkippedTransient;
    }

    // Identity fields (e.g. prefabNodeId) must persist on prefab instances even when equal to the
    // template default; only skip the default-omission when the value is actually set.
    const forceSerializeIdentity =
      metadata?.alwaysSerializeWhenSet === true &&
      value !== undefined && value !== null && value !== '';

    if (this.defaultValues && !forceSerializeIdentity) {
      const defaultValue = this.defaultValues[key];
      if (value === null || typeof value !== 'object') {
        if (value === defaultValue) {
        // skip if values strictly equals to base instance value
          return DumpValueResult.SkippedEqualToDefaultValue;
        }
      } else if (defaultValue) {
        const serializeFn = getSerializeFn(value);
        if (serializeFn && serializeFn === getSerializeFn(defaultValue)) {
          const dumpedValue = createDumper().dump(value);
          const dumpedDefaultValue = createDumper().dump(defaultValue);
          if (JSON.stringify(dumpedValue) === JSON.stringify(dumpedDefaultValue)) {
            // CAUTION: skip serializing default values by checking JSON.stringify equality
            // This could cause issues if object properties are shared references
            // e.g. THREE.Plane has normal vector that's shared in another place
            return DumpValueResult.SkippedEqualToDefaultValue;
          }
        } else if (
          // object type, do a deep-ish comparison
          Object.getPrototypeOf(value) === Object.getPrototypeOf(defaultValue)
          && deepishEqual(value, defaultValue, 3)
        ) {
          return DumpValueResult.SkippedEqualToDefaultValue;
        }
      }
    }

    return this._dumpTo(key, value, this.currentRecord!.$value as SerializedObjectNode, instanceKey, metadata) ? DumpValueResult.Dumped : DumpValueResult.SkippedOtherwise;
  }

  public removeDumpedValue(key: string) {
    const value = this.currentRecord!.$value;
    if (value !== null && typeof value === 'object' && key in value) {
      // Both object and array nodes support string-keyed delete
      delete (value as Record<string, unknown>)[key];
    }
  }

  private _dumpTo(key: string, value: unknown, target: SerializedObjectNode, instanceKey?: string | null, metadata?: PropertyMetadata): boolean {
    let dumped = false;
    this.pathStack.push(key);
    // const fullPath = this.pathStack.join('.');
    // console.log('dumping key:', fullPath);
    if (value !== undefined) {
      // NOTE: if instanceKey is undefined, use `key`; but if `instanceKey` is null, don't use any key to get default values
      const propertyName = instanceKey !== null ? (instanceKey ?? key) : null;
      const defaultValues = propertyName ? (this.defaultValues?.[propertyName] as SerializedObjectNode) : undefined;
      target[key] = this._dump(value, defaultValues, metadata?.serialize);
      dumped = true;
    }
    this.pathStack.pop();
    return dumped;
  }

  public dumpProperties(obj: any) {
    if (!shouldSerializeRespectingMetadata(obj)) {
      const isArray = Array.isArray(obj);
      // no decorated properties, do a deep dump
      const entries = isArray
        ? obj.map((val, i) => [i, val] as const)
        : Object.entries(obj);

      for (const [key, val] of entries) {
        if (!isArray && (key as string).startsWith('$')) continue;  // skip special properties, such as $prefab

        // NOTE: this will dump a dict for dict, and array for array
        // but for array, we can have empty slots, due to `dumpValue` only writes to "changed" fields
        // default: [1,2,3] -> current: [1, 2, 4] = serialized: [X, X, 4]
        // default: [1,2,3] -> current: [2, 2, 3] = serialized: [2], because slot 0 is changed to 2, and slot 1 and 2 are not modified
        // default: [1,2,3] -> current: [1] = serialized: [], because slot 0 is not written, 1 and 2 are not even iterated because they don't exist on current
        // Note the `empty slots` exist because `dumpValue` directly dumps to the requested index, `result[3] = xx`, resulting in `result[0]` and `result[1]` being an empty hole
        // This apparently can't be directly dumped, so we will handle array in next step
        this.dumpValue(String(key), val);
      }

      if (isArray) {
        // $value was initialized as [] for array objects in _dump; arrays can contain holes
        // which read back as `undefined`, so widen the element type here.
        const dumpedArray = this.currentRecord!.$value as SerializedArrayNode;
        if (
          dumpedArray.some(v => v === undefined)  // there is undefined in dumped array
          || Object.keys(dumpedArray).length !== dumpedArray.length  // there is hole in the dumped array
          || dumpedArray.length !== obj.length  // or the length is different (essentially, undefined at the end)
          || (this.defaultValues && Object.keys(this.defaultValues).length > 0)  // or there is default values
        ) {
          // we should only record the "diff" here
          // to record the diff, we also need to store the length, so that we would have:
          // [1,2,3] -> [4], with diff being {$length: 1, "0": 4}
          // [1,2,3] -> [1,4], with diff being {$length: 2, "1": 4}
          const arrayDiff: SerializedObjectNode = { $length: obj.length };
          this.currentRecord!.$value = arrayDiff;
          for (const [key, val] of Object.entries(dumpedArray)) {
            if (val !== undefined) {
              arrayDiff[key] = val;
            }
          }
        } else {
          // if all values are dumped, we can directly dump the array
          this.currentRecord!.$value = dumpedArray;
        }
      }
      return;
    }

    const properties = getAllProperties(obj);
    const objRecord = obj as Record<string, unknown>;
    for (const [key, metadata] of properties) {
      this.dumpValue(key, objRecord[key], key, metadata);
    }
  }

  public markEditorOnly() {
    if (!this.currentRecord?.$meta) {
      this.currentRecord!.$meta = {};
    }
    this.currentRecord!.$meta.editorOnly = true;
  }

  private getId(obj: object): RefType {
    if (!this.seen.has(obj)) {
      const newId = this.idCounter++;
      this.seen.set(obj, newId);
      this.refCount.set(newId, 0);
    }
    return this.seen.get(obj)!;
  }

  // valuesOverriddenByPrefab comes from the outer prefab, i.e. from the component data in an actor prefab
  private _dump(obj: unknown, valuesOverriddenByPrefab?: SerializedObjectNode, customSerialize?: (obj: unknown, dumper: Dumper) => void): Ref | SerializedValue {
    if (typeof obj !== 'object' || obj === null) {
      return obj as SerializedValue;
    }

    const objNode = obj as object;

    // Already serialized → return a reference
    if (this.seen.has(objNode)) {
      const refId = this.getId(objNode);
      if (this.refCount.get(refId) === undefined || this.refCount.get(refId) === 0) {
        throw new Error('Dumper: refCount for object does not exist at this point, this should not happen.');
      }
      this.refCount.set(refId, this.refCount.get(refId)! + 1);
      return { $ref: refId };
    }

    const defaultValues: SerializedObjectNode = {};
    const cdo = getDefaultObject(objNode);
    if (cdo) {
      // Copy own enumerable properties
      Object.assign(defaultValues, cdo);
      // Also read values from prototype getters (e.g., @property decorated getters)
      const properties = getAllPropertiesFromProto(Object.getPrototypeOf(cdo));
      for (const [key] of properties) {
        if (!(key in defaultValues)) {
          defaultValues[key] = (cdo as Record<string, unknown>)[key];
        }
      }
    }
    if (valuesOverriddenByPrefab) {
      Object.assign(defaultValues, valuesOverriddenByPrefab);
    }

    // supplement the getter properties
    if (valuesOverriddenByPrefab) {
      const proto = Object.getPrototypeOf(valuesOverriddenByPrefab);
      const properties = getAllPropertiesFromProto(proto);
      for (const [key] of properties) {
        const descriptor = Object.getOwnPropertyDescriptor(proto, key);
        if (descriptor && typeof descriptor.get === 'function') {
          defaultValues[key] = valuesOverriddenByPrefab[key];
        }
      }
    }

    const refId = this.getId(objNode);
    const record: SerializedSubObject = { $id: refId, $value: Array.isArray(obj) ? [] : {} };

    let baseInfo: BaseInfo | null = null;
    const objWithPrefab = obj as { $prefab?: { $path: string } };
    const prefabPath = objWithPrefab.$prefab?.$path;
    if (prefabPath) {
      baseInfo = { $path: prefabPath };
    }
    else {
      const clsName = getClassSerializeName(objNode);
      if (clsName) {
        baseInfo = { $class: clsName };
      } else {
        // if it's an unregistered class (exclude built-in prototypes)
        const proto = Object.getPrototypeOf(objNode);
        if (proto !== Object.prototype && proto !== Array.prototype && proto !== String.prototype && proto !== Number.prototype && proto !== Boolean.prototype) {
          const ctor = (objNode as { constructor: { name: string } }).constructor;
          baseInfo = { $class: `${UNREGISTERED_CLASS_PREFIX}${ctor.name}` };
        }
      }
    }

    if (baseInfo) {
      record.$base = baseInfo;
    }

    // actual dumping
    const prevRecord = this.currentRecord;
    this.currentRecord = record;

    const prevBaseInstance = this.defaultValues;
    this.defaultValues = defaultValues;

    // push the record first to handle circular references
    this.objects.push(record);
    this.refCount.set(refId, this.refCount.get(refId)! + 1);

    if (customSerialize) {
      customSerialize(obj, this);
    } else if (isSerializableObject(objNode) && objNode.serialize) {
      objNode.serialize(this);
    } else {
      const serializeFn = getSerializeFn(objNode);
      if (serializeFn) {
        serializeFn(objNode, this);
      } else {
        this.dumpProperties(objNode);
      }
    }
    // Dump init options after regular properties so shared objects such as
    // rootComponent get their first full serialization through the property
    // path. Otherwise init options can consume the first dump and force the
    // property to become only a $ref, which breaks prefab override diffs.
    const rawInitOptions = objectInitOptionsTracker.retrieve(objNode);
    if (!prefabPath && rawInitOptions) {
      // Prefab instances already reconstruct from $base. Persisting init
      // options here adds extra references to the same objects and prevents
      // prefab instance overrides from being inlined in the exported diff.
      record.$initOptions = this._dump(rawInitOptions);
    }
    this.currentRecord = prevRecord;
    this.defaultValues = prevBaseInstance;

    return { $ref: refId };
  }
}

class Loader extends Serializer implements ILoader {
  private objMap = new Map<RefType, object>();
  private currentObjectRecord: SerializedValue | null = null;
  private objectsData = new Map<RefType, SerializedSubObject>();
  private pathStack: string[] = [];
  private flags: LoaderFlags;
  public errors: string[] = [];
  private readonly reconcilePrefabInstanceChildren: boolean;
  /** Set during {@link _prepareLoader} when the loaded root resolves to a prefab instance (has a prefab `$base`). */
  private rootIsPrefabInstance = false;

  constructor(options?: LoaderOptions) {
    super();
    this.flags = options?.flags ?? LoaderFlags.None;
    this.reconcilePrefabInstanceChildren = options?.reconcilePrefabInstanceChildren ?? false;
  }

  /**
   * Stable prefab-node-identity reconciliation only runs while merging a prefab *instance* over its
   * freshly instantiated template. It must stay off for standalone/template loads (root is class-based),
   * where serialized children are the source of truth and dropping by missing id would be wrong.
   */
  public shouldReconcilePrefabInstanceChildren(): boolean {
    return this.reconcilePrefabInstanceChildren && this.rootIsPrefabInstance;
  }

  // public API
  public load(data: SerializedData): any {
    return this.loadToInstance(data);
  }

  public async loadAsync(data: SerializedData): Promise<any> {
    return this.loadToInstanceAsync(data);
  }

  public loadToInstance(data: SerializedData, target?: Record<string, any> | any[]): any {
    const startTime = performance.now();

    this._prepareLoader(data);
    const result = this._load(data.$root, '$root', target);
    if (this.errors.length > 0) {
      console.error(`Loader encountered ${this.errors.length} error(s) during loading:`, this.errors);
    }

    const endTime = performance.now();
    const duration = endTime - startTime;
    if (duration > 50) {
      console.log(`📦Loader: Loading took time ${duration.toFixed(2)}ms`);
    }

    return result;
  }

  public async loadToInstanceAsync(data: SerializedData, target?: Record<string, any> | any[]): Promise<any> {
    const startTime = performance.now();
    this._prepareLoader(data);

    // collect and load all prefab data first
    const traverseAndLoadPrefabs = async (data: any): Promise<void> => {
      if (data && typeof data === 'object') {
        if (data.$base && isPrefabInfo(data.$base)) {
          const prefabPath = data.$base.$path;
          let prefabData = undefined;
          try {
            prefabData = await PrefabManager.getOrLoadPrefabData(prefabPath);
          } catch (error) {
            this.errors.push(`Loader: Failed to load prefab at path ${prefabPath}: ${(error as Error).message}`);
          }

          if (prefabData) {
            // traverse the prefab data
            await traverseAndLoadPrefabs(prefabData);
          }
        }

        for (const value of Object.values(data)) {
          await traverseAndLoadPrefabs(value);
        }
      }
    };
    await traverseAndLoadPrefabs(data);

    const result = this._load(data.$root, '$root', target);
    const endTime = performance.now();
    console.log(`📦Loader: Async loading took time ${(endTime - startTime).toFixed(2)}ms`);
    return result;
  }

  public hasFlags(flags: LoaderFlags): boolean {
    return (this.flags & flags) === flags;
  }
  // end public API

  private _prepareLoader(data: SerializedData) {
    if (this.objMap.size > 0 || this.objectsData.size > 0 || this.currentObjectRecord || this.pathStack.length > 0) {
      throw new Error('Loader instances cannot be reused.');
    }

    if (data[serializationVersionKey] > VERSION) {
      throw new Error(`Loader: Cannot load data with version ${data[serializationVersionKey]}, current supported version is ${VERSION}.`);
    }

    if (data[serializationVersionKey] < SerializationVersion.V1) {
      throw new Error(`Loader: Not capable of loading legacy version ${data[serializationVersionKey]}, current supported version range is ${SerializationVersion.V1} - ${VERSION}.`);
    }

    if (data.$objects) {
      for (const record of data.$objects) {
        this.objectsData.set(record.$id!, record);
      }
    }

    // convert $bc -> {$base: {$class: ...} }
    const extractClassBaseInfo = (obj: SerializedValue): void => {
      if (typeof obj !== 'object' || obj === null) {
        return;
      }
      const objNode = obj as SerializedObjectNode;
      for (const [k, v] of Object.entries(objNode)) {
        if (v && typeof v === 'object' && '$bc' in v) {
          const childNode = v as SerializedObjectNode;
          const className = childNode.$bc;
          childNode.$base = { $class: className! };
          delete childNode.$bc;
        }
        extractClassBaseInfo(v as SerializedValue);
      }
    };
    extractClassBaseInfo(data);

    this.rootIsPrefabInstance = this._rootResolvesToPrefabInstance(data.$root);
  }

  /** Whether `$root` (inline or `$ref`) carries a prefab `$base`, i.e. the load target is a prefab instance. */
  private _rootResolvesToPrefabInstance(root: SerializedValue): boolean {
    if (!root || typeof root !== 'object' || Array.isArray(root)) {
      return false;
    }
    let node: SerializedValue = root;
    if (_isRef(root)) {
      const record = this.objectsData.get(root.$ref);
      if (record?.$base) {
        return isPrefabInfo(record.$base);
      }
      if (!record) {
        return false;
      }
      node = record.$value;
    }
    const base = (node as SerializedObjectNode)?.$base;
    return !!base && isPrefabInfo(base);
  }

  private findInstance(id: RefType | null): any | null {
    if (id !== null && this.objMap.has(id)) {
      return this.objMap.get(id) ?? null;
    }
    return null;
  }

  private loadInitOptions(initOptions: unknown): unknown {
    if (isSerializedData(initOptions)) {
      // Merge nested object records into this loader so refs inside init
      // options resolve against the same object map as the owning actor.
      if (initOptions.$objects) {
        for (const record of initOptions.$objects) {
          this.objectsData.set(record.$id!, record);
        }
      }
      return this._load(initOptions.$root, '$initOptions');
    }
    return this._load(initOptions as SerializedValue, '$initOptions');
  }

  private findOrCreateInstance(baseInfo: BaseInfo | undefined, propertiesData: any, id: RefType | null, initOptions?: unknown): NonNullable<any> {
    // Already created
    const existingInstance = this.findInstance(id);
    if (existingInstance) {
      return existingInstance;
    }

    if (baseInfo && isPrefabInfo(baseInfo)) {
      try {
        return instantiatePrefab(baseInfo.$path);
      } catch (error) {
        if (error instanceof Error) {
          // Could happen due to prefab renames, we don't want to crash the game/editor
          console.warn(`Loader: Failed to instantiate prefab at path ${baseInfo.$path}: ${error.message}. Giving up.`);
          throw error;
        } else {
          throw error;
        }
      }
    }

    // If it's a class instance
    const clsName = baseInfo && isClassInfo(baseInfo) ? baseInfo.$class : undefined;
    const isIdValid = id !== null;
    if (clsName) {
      const cls = getClassForDeserialization(clsName);
      const instance = this._tryStaticDeserialize(clsName, propertiesData);
      if (instance) {
        if (isIdValid) this.objMap.set(id, instance);
        return instance;
      }

      if (!cls) {
        // class not found, leave a warning here
        console.warn(`Loader: Class not found for name ${clsName}, creating plain object instead.`);
      }
      else {
        if (isActorConstructor(cls)) {
          // Actor init options may reference the actor itself or components in
          // the same serialized graph. Allocate and register the actor first,
          // then load options in this loader before running initialize().
          const instance = new cls();
          if (isIdValid) {
            this.objMap.set(id, instance);
          }
          const loadedInitOptions = initOptions !== undefined ? this.loadInitOptions(initOptions) : undefined;
          instance.initialize(loadedInitOptions);
          objectInitOptionsTracker.record(instance, loadedInitOptions);
          return instance;
        }
        const instance = new cls();
        if (isIdValid) {
          this.objMap.set(id, instance);
        }
        return instance;
      }
    }

    const instance = Array.isArray(propertiesData) ? [] : {};
    if (isIdValid) {
      this.objMap.set(id, instance);
    }
    return instance;
  }

  private _populate(value: any, target: any, baseInfo: BaseInfo | null, metadata?: PropertyMetadata): void {
    const prevObjectRecord = this.currentObjectRecord;
    this.currentObjectRecord = value;

    // target ??= Array.isArray(value) ? [] : {};

    if (metadata?.deserialize) {
      // if metadata has custom deserialize function, use it
      metadata.deserialize(target, this);
    } else if (isSerializableObject(target) && target.deserialize) {
      // If it's a class instance and implements ISerializableObject, call its serialize method
      target.deserialize(this);
    } else {
      const deserializeFn = getDeserializeFn(target);
      if (deserializeFn) {
        deserializeFn(target, this);
      } else {
        this.loadProperties(target);
      }
    }

    if (baseInfo && isPrefabInfo(baseInfo) && baseInfo.$path) {
      target.$prefab = { $path: baseInfo.$path };
    }

    this.currentObjectRecord = prevObjectRecord;
  }

  private _matchesExisting(existing: any, refId: RefType | undefined, className: string | null, dataValue: any): boolean {
    if (refId !== undefined && this.objMap.has(refId)) {
      // if it's a referenced object, must match by identity
      return this.objMap.get(refId) === existing;
    }
    if (Array.isArray(existing) && Array.isArray(dataValue)) {
      return true;
    }
    if (className) {
      const cls = getClassForDeserialization(className);
      if (cls) {
        // registered class
        if (!(existing instanceof cls)) return false;
        const existingInstanceClsName = getClassSerializeName(existing);
        if (existingInstanceClsName !== className) return false;

        // special handling for any class that won't suffice by just copying the properties
        if (!allowPopulateExistingInstance(existing.constructor)) return false;

        // There should be an absolute truth here to tell if the serialized data matches the existing instance
        // For now I can only guess
        if (existing.name && dataValue?.name && existing.name !== dataValue.name) {
          return false;
        }

        // return true here for now
        return true;
      } else {
        if (className.startsWith(UNREGISTERED_CLASS_PREFIX)) {
          // unregistered class, just check the constructor name
          const expectedCtorName = className.substring(UNREGISTERED_CLASS_PREFIX.length);
          return existing.constructor.name === expectedCtorName;
        }
      }
    } else if (typeof existing === 'object' && existing !== null && !Array.isArray(existing)) {
      // plain object, return true because we have no way to tell its default state, so basically treating the existing instance as "default"
      return true;
    }

    return false;
  }

  // target is the existing object to resolve into if it's able to
  // it is required so as to avoid re-creating objects that are already present
  public loadValue(key: string, target?: any, metadata?: PropertyMetadata): any {
    return this._load((this.currentObjectRecord as SerializedObjectNode)[key], key, target, metadata);
  }

  private _findClass(baseInfo: BaseInfo, seen?: Set<string>): string | null {
    if (isClassInfo(baseInfo)) {
      return baseInfo.$class;
    }

    seen ??= new Set<string>();

    const prefabPath = isPrefabInfo(baseInfo) ? baseInfo.$path : null;
    if (prefabPath) {
      if (seen.has(prefabPath)) {
        throw new Error(`Loader: Circular prefab reference detected for prefab ${prefabPath}, aborting class lookup.`);
      }
      seen.add(prefabPath);
      const baseInfo = PrefabManager.getPrefabBaseInfo(prefabPath);
      if (baseInfo) {
        return this._findClass(baseInfo, seen);
      }
    }

    return null;
  }

  // This only works for specialized classes that can be directly constructed using the data provided
  private _tryStaticDeserialize(clsName: string, data: any): any | null {
    const staticDeserializeFn = getStaticDeserializeFn(clsName);
    if (staticDeserializeFn) {
      let instance = null;
      if (typeof data !== 'object' || data === null) {
        // data is primitive type, such as a single number or string
        instance = staticDeserializeFn(data, this);
      }
      else {
        const prev = this.currentObjectRecord;
        this.currentObjectRecord = data;
        instance = staticDeserializeFn(data, this);
        this.currentObjectRecord = prev;
      }

      return instance;

    }
  }

  private _isArrayDumpedAsObject(data: any): boolean {
    // return true, if `data` contains $length, and all its other fields are numeric strings
    if (data && typeof data === 'object' && '$length' in data) {
      for (const key of Object.keys(data)) {
        if (key === '$length') continue;
        if (isNaN(Number(key))) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  // key is only for path tracking, but it's required just to be clear
  private _load(serializedValue: any, key: string, target?: any, metadata?: PropertyMetadata): any {
    const _internalLoad = (serializedValue: any, target: any, metadata: PropertyMetadata) => {
      if (Array.isArray(serializedValue) || this._isArrayDumpedAsObject(serializedValue)) {
        // if `serializedValue` is array, `target` may or may not be an array. Usually it's not, but if calling `loadInstanceTo(data, [])`, then it can can be an array
        if (Array.isArray(serializedValue)) {
          const result = serializedValue.map((v, index) => {
            // Try custom resolver first if available
            let elementTarget: any = undefined;
            if (Array.isArray(target)) {
              elementTarget = target[index];
              if (metadata?.resolveArrayElement) {
                // Pass dereferenced data (actual object content) to resolver
                let dataValue = v;
                if (_isRef(v)) {
                  const record = this.objectsData.get(v.$ref);
                  dataValue = record?.$value ?? v;
                }
                const resolved = metadata.resolveArrayElement(dataValue, target, index);
                if (resolved === STALE_PREFAB_CHILD_ARRAY_SLOT) {
                  return STALE_PREFAB_CHILD_ARRAY_SLOT;
                }
                elementTarget = resolved ?? elementTarget;
              }
            }
            return this._load(serializedValue[index], String(index), elementTarget);
          });
          if (Array.isArray(target)) {
            // push into the items that's not already present
            const seen = new Set(target);
            target.push(...result.filter(item => item !== STALE_PREFAB_CHILD_ARRAY_SLOT && !seen.has(item)));
            return target;
          }
          return result.filter(item => item !== STALE_PREFAB_CHILD_ARRAY_SLOT);
        }
        else if (this._isArrayDumpedAsObject(serializedValue)){
          // if `serializedValue` is a delta, that contains { index -> overriddenValue }
          const result = Array.isArray(target) ? target : [];
          for (const [k, v] of Object.entries(serializedValue)) {
            if (k === '$length') continue;
            const index = Number(k);

            // Try custom resolver first if available
            let elementTarget: any = result[index];
            if (Array.isArray(target) && metadata?.resolveArrayElement) {
              // Pass dereferenced data (actual object content) to resolver
              let dataValue = serializedValue[k];
              if (_isRef(dataValue)) {
                const record = this.objectsData.get(dataValue.$ref);
                dataValue = record?.$value ?? dataValue;
              }
              const resolved = metadata.resolveArrayElement(dataValue, target, index);
              if (resolved === STALE_PREFAB_CHILD_ARRAY_SLOT) {
                continue;
              }
              if (resolved !== undefined) {
                elementTarget = resolved;
              }
            }

            const loaded = this._load(serializedValue[k], String(k), elementTarget);

            if (metadata?.resolveArrayElement && elementTarget !== undefined && Array.isArray(target)) {
              const pos = target.indexOf(elementTarget);
              if (pos !== -1 && pos !== index) {
                // Merged in-place into a different index than the serialized slot (e.g. prefab
                // template dropped an earlier child). Avoid assigning `loaded` at `index`, which
                // would duplicate the subtree or leave a wrong slot.
                continue;
              }
            }

            result[index] = loaded;
          }

          if (serializedValue.$length !== undefined) {
            result.length = serializedValue.$length;
            if (metadata?.resolveArrayElement) {
              let end = result.length;
              while (end > 0 && result[end - 1] === undefined) {
                end--;
              }
              if (end !== result.length) {
                result.length = end;
              }
            }
          }

          // If all items were transient, the array contains only undefined values.
          // Compact it to an empty array to avoid undefined slots in the loaded data.
          if (result.length > 0 && result.every(v => v === undefined)) {
            return [];
          }
          return result;
        }
      }
      else if (!serializedValue || typeof serializedValue !== 'object') {
        return serializedValue;
      } else {
        // handle object, two possible cases for `serializedValue`:
        // a ref obj: { $ref: 1 }
        // full serialized object: { $value: {...}, $class?: 'ClassName' }, note $class is optional
        // it's always two steps: instantiate the object (findOrCreateInstance), then populate its properties (_populate)

        let referencedObject: SerializedSubObject | null = null;
        if (_isRef(serializedValue)) {
          const refId = serializedValue.$ref;
          const record = this.objectsData.get(refId);
          if (!record) throw new Error(`Loader: No object record found for ref ${refId}`);
          referencedObject = record;
        }

        const existingInstance = this.findInstance(referencedObject?.$id ?? null);
        if (existingInstance) {
          return existingInstance;
        }

        const data = referencedObject ? referencedObject.$value : serializedValue;
        const meta = referencedObject ? referencedObject.$meta : serializedValue.$meta;
        if (meta?.editorOnly && this.hasFlags(LoaderFlags.WithoutEditor)) {
          return null;
        }
        const baseInfo: BaseInfo | undefined = referencedObject ? referencedObject.$base : serializedValue.$base;

        // if the instance doesn't exist yet, and it's not required to populate value onto a given target
        // we can give user a chance to construct and populate an instance using `data` and `cls`
        // but for now let's only use it internally by specialization
        if (!target) {
          const cls: string | undefined = baseInfo && isClassInfo(baseInfo) ? baseInfo.$class : undefined;
          if (cls) {
            const instance = this._tryStaticDeserialize(cls, data);
            if (instance) {
              // it's important to always add to objMap to prevent circular reference issues
              if (referencedObject && !this.objMap.has(referencedObject.$id!)) {
                this.objMap.set(referencedObject.$id!, instance);
              }
              return instance;
            }
          }
        }

        // should we directly load into target, or should we create a new instance
        // this is common case for loading into a component on actor, which is also created in the actor's ctor
        const cls = baseInfo ? this._findClass(baseInfo) : null;
        const loadIntoExisting = target && this._matchesExisting(target, referencedObject?.$id, cls, serializedValue);
        let result = undefined;
        if (loadIntoExisting) {
          result = target;
        }
        else {
          try {
          // objMap is set in findOrCreateInstance
            result = this.findOrCreateInstance(
              baseInfo,
              data,
              referencedObject?.$id ?? null,
              referencedObject?.$initOptions ?? data.$initOptions
            );
          } catch (error) {
            this.errors.push((error as Error).message);
            return undefined;
          }
        }

        // must do it prior to _populate to avoid circular reference issues
        if (referencedObject && !this.objMap.has(referencedObject.$id!)) {
          this.objMap.set(referencedObject.$id!, result);
        }

        this._populate(data, result, baseInfo ?? null, metadata);
        if (isSerializableObject(result) && result.postLoad) {
          result.postLoad();
        }
        return result;
      }
    };

    this.pathStack.push(key);
    // // skip the first $root
    // const fullPath = this.pathStack.slice(1).join('.');
    // console.log('loading key:', fullPath);
    const loaded = _internalLoad(serializedValue, target, metadata ?? {});
    this.pathStack.pop();
    return loaded;
  }

  public loadProperties(obj: any) {
    if (!this.currentObjectRecord) {
      throw new Error('Loader: currentObjectRecord is null when calling loadProperties');
    }
    const record = this.currentObjectRecord as SerializedObjectNode;

    if (!shouldSerializeRespectingMetadata(obj)) {
      const modifiedKeys = new Set();
      for (const [key, val] of Object.entries(record)) {
        if (key.startsWith('$')) continue;  // skip the special fields
        modifiedKeys.add(key);
        const loaded = this._load(record[key], key, obj[key]);
        if (loaded === obj[key]) continue;  // skip assignment if same
        obj[key] = loaded;
      }
      return;
    }

    const properties = getAllProperties(obj);
    for (const [key, metadata] of properties) {
      const oldKey = metadata?.migrateFrom;
      let data = oldKey ? record[oldKey] : undefined;
      if (data === undefined) {
        data = record[key];
      }
      if (data !== undefined) {
        if (metadata?.editorOnly && this.hasFlags(LoaderFlags.WithoutEditor)) {
          // skip editorOnly properties when not loading with editor
          // but should I mark the property as undefined on the object?
          continue;
        }
        // don't worry about transient properties here, they are already skipped when dumping
        const result = this._load(data, key, obj[key], metadata);
        if (result !== obj[key]) {
          obj[key] = result;
        }
      }
    }
  }
}

registerLoader(Loader);
registerDumper(Dumper);
